import { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { FormSpy } from 'react-final-form';
import { getIn } from 'final-form';
import { v4 as uuidv4 } from 'uuid';
import { isNil } from 'ramda';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmIsNeedActSaveOrSign } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmErknmViolationEvidence } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmViolationEvidence';
import { Row } from 'UIPackage/FormModule/components/Row';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { checkHasErrorInCollapseBlock } from 'functions/checkHasErrorInCollapseBlock';
import { TextInputEditableLinkField } from 'UIPackage/MainModule/components/TextInputEditableLinkField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';

import { Button } from '../../../../UIPackage/MainModule/components/Button';
import styles from './ErknmEvidence.module.scss';
import { getUserRoleIsSupervisor } from '../../../../AuthPackage/MainModule/selectors/authSelectors';

interface IErknmEvidenceProps {
  id?: string;
  name: string;
  watchOnly: boolean;
  formRef?: IKnmFormRef | IDecisionFormRef;
  isDecision?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  onlyLinkView?: boolean;
  disableOriginPassportLinkEditable?: boolean;
  updateAction?: () => void;
  maxDate?: Date;
  minDate?: Date;
}

interface IErknmEvidenceStoreProps {
  isSupervisor: boolean;
  isNeedActSaveOrSign?: boolean;
}

const ErknmEvidenceWithoutStore = ({
  id,
  isSupervisor,
  formRef,
  name,
  watchOnly,
  isDecision,
  isKnm,
  isPm,
  isNeedActSaveOrSign = false,
  onlyLinkView,
  disableOriginPassportLinkEditable,
  maxDate,
  minDate,
  updateAction,
}: IErknmEvidenceProps & IErknmEvidenceStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const fieldName = name;

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addEvidence = useCallback(() => {
    if (!watchOnly && push) {
      const addingEvidence: IKnmErknmViolationEvidence = {
        guid: null,
        date: undefined,
        description: undefined,
        link: undefined,
        listId: uuidv4(),
        isNew: true,
      };

      push(fieldName, addingEvidence);
    }
  }, [fieldName, push, watchOnly]);

  const makeRemoveEvidenceHandler = (index: number) => () => !watchOnly && remove && remove(fieldName, index);

  return (
    <FieldBlock
      id={id}
      label={getTitleFieldWithRedStar({
        label: 'Ссылка на видеозапись или фотоматериалы',
        field: fieldName,
        isDecision,
        isKnm,
        isPm,
      })}
      button={
        <div className={styles.ActionButtonsWrapper}>
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={addEvidence}
            disabled={!isSupervisor || watchOnly || isNeedActSaveOrSign}
          />
          {updateAction && (
            <Button
              className="shared-btn-add"
              text="Обновить"
              type="button"
              variant="link"
              color="primary"
              onClick={updateAction}
              disabled={!isSupervisor || watchOnly || isNeedActSaveOrSign}
            />
          )}
        </div>
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          if (!fieldName || !props.errors) return null;

          const fieldError = getIn(props.errors, fieldName);
          const isFieldTouched = props.touched?.[fieldName];

          if (fieldError === REQUIRED_ERROR && isFieldTouched) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={fieldName}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmViolationEvidence, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((evidenceFieldName, sIndex) => {
                const fieldValue: IKnmErknmViolationEvidence = fields.value?.[sIndex];
                const baseId = `${id}[${fieldValue?.listId}]`;

                const isDisabledByOriginPassportLink =
                  !isNil(fieldValue?.originPassportId) && disableOriginPassportLinkEditable;

                const hasError = checkHasErrorInCollapseBlock(formRef, evidenceFieldName);

                return !onlyLinkView ? (
                  <KnmCollapse
                    id={baseId}
                    key={`evidence-row-${sIndex}`}
                    title={
                      <span className={'shared-break-word'}>
                        <span>Ссылка на видеозапись или фотоматериалы №{sIndex + 1}:</span>{' '}
                      </span>
                    }
                    opened={fieldValue.isNew}
                    onClose={
                      !isDisabledByOriginPassportLink && !watchOnly ? makeRemoveEvidenceHandler(sIndex) : undefined
                    }
                    isNew={fieldValue.isNew}
                    changeOpenedCallback={e =>
                      setKnmCollapseOpened(state => ({
                        ...state,
                        [evidenceFieldName]: e,
                      }))
                    }
                    error={hasError && knmCollapseOpened[evidenceFieldName]}
                  >
                    <>
                      <Row>
                        <FieldBlock
                          id={`${baseId}.description`}
                          label={getTitleFieldWithRedStar({
                            label: 'Фиксация потенциального нарушения',
                            field: `${evidenceFieldName}.description`,
                            isDecision,
                            isKnm,
                            isPm,
                          })}
                          color="black"
                          placementLabel="left"
                        >
                          <TextInputField
                            className={styles.DescriptionField}
                            name={`${evidenceFieldName}.description`}
                            placeholder="Опишите нарушение"
                            disabled={watchOnly || isDisabledByOriginPassportLink}
                          />
                        </FieldBlock>
                      </Row>
                      <Row>
                        <FieldBlock
                          id={`${baseId}.link`}
                          label={getTitleFieldWithRedStar({
                            label: 'Ссылка на видеозапись или фотоматериалы',
                            field: `${evidenceFieldName}.link`,
                            isDecision,
                            isKnm,
                            isPm,
                          })}
                          color="black"
                          placementLabel="left"
                        >
                          <TextInputEditableLinkField
                            className={styles.LinkField}
                            name={`${evidenceFieldName}.link`}
                            placeholder="Вставьте ссылку на материалы"
                            disabled={watchOnly || isDisabledByOriginPassportLink}
                            size="medium"
                          />
                        </FieldBlock>
                      </Row>
                      <Row>
                        <FieldBlock
                          id={`${baseId}.date`}
                          label={getTitleFieldWithRedStar({
                            label: 'Дата фиксации',
                            field: `${evidenceFieldName}.date`,
                            isDecision,
                            isKnm,
                            isPm,
                          })}
                          color="black"
                          placementLabel="left"
                        >
                          <DatePickerField
                            className={styles.DateField}
                            id={`${evidenceFieldName}.date`}
                            name={`${evidenceFieldName}.date`}
                            placeholder="Выберите дату"
                            disabled={watchOnly || isDisabledByOriginPassportLink}
                            maxDate={maxDate}
                            minDate={minDate}
                          />
                        </FieldBlock>
                      </Row>
                    </>
                  </KnmCollapse>
                ) : (
                  <div className={styles.OnlyLinkViewWrapper}>
                    <FieldBlock id={`${baseId}.link`} color="black" placementLabel="left">
                      <TextInputEditableLinkField
                        className={styles.LinkField}
                        name={`${evidenceFieldName}.link`}
                        placeholder="Вставьте ссылку на материалы"
                        disabled={watchOnly || isDisabledByOriginPassportLink}
                        size="medium"
                      />
                    </FieldBlock>
                    {!(watchOnly || isDisabledByOriginPassportLink) && (
                      <CloseButton className={styles.DeleteButton} onClick={makeRemoveEvidenceHandler(sIndex)} />
                    )}
                  </div>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmEvidenceStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
});

export const ErknmEvidence = connect(mapStateToProps)(ErknmEvidenceWithoutStore);
